// Utility functions for LeadMap3D

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { Business, Coordinates, LeadFilter } from '../types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}

export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)}m`;
  }
  return `${(meters / 1000).toFixed(1)}km`;
}

export function formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...options,
  });
}

export function formatRelativeTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(d);
}

export function calculateDistance(coord1: Coordinates, coord2: Coordinates): number {
  const R = 6371e3; // Earth radius in meters
  const φ1 = (coord1.lat * Math.PI) / 180;
  const φ2 = (coord2.lat * Math.PI) / 180;
  const Δφ = ((coord2.lat - coord1.lat) * Math.PI) / 180;
  const Δλ = ((coord2.lng - coord1.lng) * Math.PI) / 180;
  
  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) *
    Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
  return R * c;
}

export function getLeadTypeLabel(type: Business['leadType']): string {
  const labels: Record<Business['leadType'], string> = {
    'no-website': 'No Website',
    'marketing': 'Needs Marketing',
    'b2b': 'B2B Lead',
    'multiple': 'Multiple Needs',
  };
  return labels[type] || type;
}

export function getLeadTypeColor(type: Business['leadType']): string {
  const colors: Record<Business['leadType'], string> = {
    'no-website': 'bg-red-100 text-red-800',
    'marketing': 'bg-amber-100 text-amber-800',
    'b2b': 'bg-blue-100 text-blue-800',
    'multiple': 'bg-purple-100 text-purple-800',
  };
  return colors[type] || 'bg-slate-100 text-slate-800';
}

export function getWebsiteStatusLabel(status: Business['websiteStatus']): string {
  const labels: Record<Business['websiteStatus'], string> = {
    'none': 'No Website',
    'outdated': 'Outdated Site',
    'modern': 'Modern Site',
    'unknown': 'Unknown',
  };
  return labels[status] || status;
}

export function getWebsiteStatusColor(status: Business['websiteStatus']): string {
  const colors: Record<Business['websiteStatus'], string> = {
    'none': 'bg-red-100 text-red-800',
    'outdated': 'bg-amber-100 text-amber-800',
    'modern': 'bg-green-100 text-green-800',
    'unknown': 'bg-slate-100 text-slate-800',
  };
  return colors[status] || 'bg-slate-100 text-slate-800';
}

export function getCategoryIcon(category: string): string {
  const icons: Record<string, string> = {
    'restaurant': '🍽️',
    'cafe': '☕',
    'bar': '🍻',
    'retail': '🛍️',
    'health': '🏥',
    'beauty': '💄',
    'fitness': '💪',
    'automotive': '🚗',
    'home': '🏠',
    'professional': '💼',
    'education': '📚',
    'entertainment': '🎮',
    'travel': '✈️',
    'finance': '💰',
    'legal': '⚖️',
    'real-estate': '🏘️',
    'technology': '💻',
    'construction': '🏗️',
    'manufacturing': '🏭',
    'wholesale': '📦',
    'transportation': '🚚',
  };
  return icons[category.toLowerCase()] || '🏢';
}

export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

export function filterLeads(leads: Business[], filters: LeadFilter): Business[] {
  return leads.filter((lead) => {
    // Category filter
    if (filters.categories.length > 0 && !filters.categories.includes(lead.category)) {
      return false;
    }
    
    // Lead type filter
    if (filters.leadTypes.length > 0 && !filters.leadTypes.includes(lead.leadType)) {
      return false;
    }
    
    // Lead score filter
    if (lead.leadScore < filters.minLeadScore || lead.leadScore > filters.maxLeadScore) {
      return false;
    }
    
    // Website filter
    if (filters.hasWebsite !== null && lead.hasWebsite !== filters.hasWebsite) {
      return false;
    }
    
    // Rating filter
    if (lead.rating !== undefined) {
      if (lead.rating < filters.minRating || lead.rating > filters.maxRating) {
        return false;
      }
    } else if (filters.minRating > 0) {
      return false;
    }
    
    // Reviews filter
    if (lead.reviewCount !== undefined) {
      if (lead.reviewCount < filters.minReviews || lead.reviewCount > filters.maxReviews) {
        return false;
      }
    } else if (filters.minReviews > 0) {
      return false;
    }
    
    // Distance filter (if we have center)
    if (filters.center) {
      const distance = calculateDistance(filters.center, lead.coordinates);
      if (distance > filters.radius) {
        return false;
      }
    }
    
    // Keywords filter
    if (filters.keywords.length > 0) {
      const searchText = `${lead.name} ${lead.category} ${lead.address} ${lead.description || ''}`.toLowerCase();
      const hasKeyword = filters.keywords.some((kw) => searchText.includes(kw.toLowerCase()));
      if (!hasKeyword) return false;
    }
    
    return true;
  });
}

export function sortLeads(leads: Business[], sortBy: string, sortOrder: 'asc' | 'desc'): Business[] {
  const sorted = [...leads].sort((a, b) => {
    let aVal: any, bVal: any;
    
    switch (sortBy) {
      case 'leadScore':
        aVal = a.leadScore;
        bVal = b.leadScore;
        break;
      case 'name':
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
        break;
      case 'rating':
        aVal = a.rating || 0;
        bVal = b.rating || 0;
        break;
      case 'reviews':
        aVal = a.reviewCount || 0;
        bVal = b.reviewCount || 0;
        break;
      case 'distance':
        // Would need center point - skip for now
        return 0;
      case 'createdAt':
        aVal = new Date(a.createdAt).getTime();
        bVal = new Date(b.createdAt).getTime();
        break;
      default:
        return 0;
    }
    
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });
  
  return sorted;
}

export function getLeadScoreColor(score: number): string {
  if (score >= 80) return 'text-green-600 bg-green-100';
  if (score >= 60) return 'text-blue-600 bg-blue-100';
  if (score >= 40) return 'text-amber-600 bg-amber-100';
  return 'text-red-600 bg-red-100';
}

export function getLeadScoreLabel(score: number): string {
  if (score >= 80) return 'Hot Lead';
  if (score >= 60) return 'Warm Lead';
  if (score >= 40) return 'Cool Lead';
  return 'Cold Lead';
}

export function validateEmail(email: string): boolean {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

export function validatePhone(phone: string): boolean {
  const re = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
  return re.test(phone);
}

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
}

export function slugify(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function copyToClipboard(text: string): Promise<void> {
  return navigator.clipboard.writeText(text);
}

export function downloadJSON(data: any, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function downloadCSV(leads: Business[], filename: string): void {
  const headers = [
    'Name',
    'Category',
    'Address',
    'Phone',
    'Email',
    'Website',
    'Website Status',
    'Rating',
    'Reviews',
    'Lead Score',
    'Lead Type',
    'Latitude',
    'Longitude',
  ];
  
  const rows = leads.map((lead) => [
    lead.name,
    lead.category,
    lead.address,
    lead.phone || '',
    lead.email || '',
    lead.website || '',
    lead.websiteStatus,
    lead.rating?.toString() || '',
    lead.reviewCount?.toString() || '',
    lead.leadScore.toString(),
    lead.leadType,
    lead.coordinates.lat.toString(),
    lead.coordinates.lng.toString(),
  ]);
  
  const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
  
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}